/**
 * Header Component
 * Main navigation header with responsive menu
 */

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, Settings, Menu, X } from 'lucide-react';
import { useAuth, useBrand } from '@/app/providers';
import { useUpcomingOpenHouseCount } from '@/hooks';
import { PATHS } from '@/routes/paths';
import { menuItems, mobileMenuItems } from './data';

export function Header() {
    const { isAuthenticated, isLoading, isAdmin } = useAuth();
    const { brand } = useBrand();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();
    const openHouseCount = useUpcomingOpenHouseCount();

    // Hide the "Open Houses" link entirely when none are scheduled. The
    // page is meant to advertise houses Shayaan is actively hosting; an
    // always-on link that lands on an empty page is just noise.
    const hideOpenHouse = openHouseCount === 0;
    const visibleMenuItems = hideOpenHouse
        ? menuItems.filter((m) => m.path !== PATHS.OPEN_HOUSE)
        : menuItems;
    const visibleMobileMenuItems = hideOpenHouse
        ? mobileMenuItems.filter((m) => m.path !== PATHS.OPEN_HOUSE)
        : mobileMenuItems;

    const getAccountPath = () => {
        if (!isAuthenticated) return PATHS.AUTH.LOGIN;
        return isAdmin ? PATHS.ADMIN.DASHBOARD : PATHS.PROFILE;
    };

    const isActivePath = (path: string) => {
        if (path === PATHS.HOME) return location.pathname === PATHS.HOME;
        return location.pathname.startsWith(path);
    };

    return (
        <header className="w-full">
            <nav className="fixed w-full top-0 z-50 bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-2 sm:py-4">
                        {/* Logo — sourced from the active brand so
                            it flips the moment the operator activates
                            a different brokerage. Falls back to the
                            seeded Century 21 GIF when the brand row
                            doesn't carry a logo. */}
                        <Link to={PATHS.HOME} className="flex-shrink-0">
                            <div className="w-24 h-[60px] sm:w-32 sm:h-[80px] relative">
                                <img
                                    src={brand.logo_url || '/Images/logo-transparent.gif'}
                                    alt={brand.name}
                                    className="w-full h-full object-contain object-left"
                                />
                            </div>
                        </Link>

                        {/* Desktop Navigation */}
                        <div className="hidden lg:flex items-center gap-1">
                            {visibleMenuItems.map((item) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                        isActivePath(item.path)
                                            ? 'text-primary-dark bg-primary/10'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                    }`}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </div>

                        {/* Right Side: Account + Mobile Menu Toggle */}
                        <div className="flex items-center gap-2">
                            {/* Account Management Button */}
                            {isLoading ? (
                                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Link
                                    to={getAccountPath()}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dark transition-colors"
                                >
                                    {isAdmin ? <Settings size={16} /> : <User size={16} />}
                                    <span className="hidden sm:inline">
                                        {isAdmin ? 'Admin Panel' : 'My Agreement'}
                                    </span>
                                </Link>
                            )}

                            {/* Mobile Menu Toggle */}
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
                            >
                                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                            </button>
                        </div>
                    </div>

                    {/* Mobile Navigation */}
                    {isMobileMenuOpen && (
                        <div className="lg:hidden border-t py-4">
                            <div className="flex flex-col gap-1">
                                {visibleMobileMenuItems.map((item) => (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className={`px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                                            isActivePath(item.path)
                                                ? 'text-primary-dark bg-primary/10'
                                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                        }`}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </nav>
            {/* Spacer for fixed header */}
            <div className="h-16 sm:h-24" />
        </header>
    );
}

export default Header;
